import { create } from 'zustand'
import {
  AvailableLanguagesType,
  ConfigType,
  InitialConfig,
  MenuConfigType,
  SELECTED_BRANCH_ID,
  TableReservationConfigType
} from './types'
import { GET } from 'shared/api/GET'
import { UpdateInterfacePayloadType } from 'shared/api/types'
import { POST } from 'shared/api/POST'
import { useBranchStore } from './branchesSlice'
import { handleLocalStorage } from 'shared/helpers/handleLocalStorage'
import { useLoadingStore } from './toasterSlice'
import { consoleError } from 'shared/helpers/consoleError'
import { LARGE_IMAGE } from 'shared/api/instance'

const LARGE_IMAGE_ERROR_MESSAGE =
  'Fehler: Die ausgewählte Bilddatei ist zu groß. Bitte wählen Sie eine kleinere Datei aus oder reduzieren Sie die Bildauflösung.'

const getInitialConfigStore = () => ({
  isConfigLoading: false,
  isConfigLoaded: false,
  config: InitialConfig
})

export const useConfigStore = create<IConfigStore>((set, getStore) => ({
  ...getInitialConfigStore(),
  loadConfig: async (branchIdParam?: number) => {
    if (!getStore().isConfigLoading) {
      const { startLoading, resetLoading } = useLoadingStore.getState()
      set(state => {
        return {
          ...state,
          isConfigLoading: true
        }
      })
      if (branchIdParam) {
        startLoading()
      }
      const branchId = useBranchStore.getState().branchId
      const branchIdLS = handleLocalStorage(SELECTED_BRANCH_ID)
      try {
        const config = await GET.config(branchIdParam || branchId || branchIdLS)
        set(state => {
          return {
            ...state,
            config,
            isConfigLoaded: true
          }
        })
      } catch (error) {
        consoleError(error)
      } finally {
        resetLoading()
        set(state => {
          return {
            ...state,
            isConfigLoading: false
          }
        })
      }
    }
  },
  updateMenuConfig: async (payload: Partial<MenuConfigType>) => {
    const { startLoading, finishWithSuccess, finishWithError } = useLoadingStore.getState()
    set(state => {
      return {
        ...state,
        isConfigLoading: true
      }
    })
    startLoading()
    try {
      const { menuConfig } = await POST.updateConfig(payload)
      set(state => {
        return {
          ...state,
          config: {
            ...state.config,
            menuConfig
          }
        }
      })
      finishWithSuccess()
    } catch (error: any) {
      consoleError(error)
      finishWithError()
    } finally {
      set(state => {
        return {
          ...state,
          isConfigLoading: false
        }
      })
    }
  },
  updateLanguagesConfig: async (language: AvailableLanguagesType | 'default') => {
    const { startLoading, finishWithSuccess, finishWithError } = useLoadingStore.getState()
    set(state => {
      return {
        ...state,
        isConfigLoading: true
      }
    })
    startLoading()
    try {
      const { languagesConfig } = await POST.updateLanguage(language)
      set(state => {
        return {
          ...state,
          config: {
            ...state.config,
            languagesConfig
          }
        }
      })
      finishWithSuccess()
    } catch (error: any) {
      consoleError(error)
      finishWithError()
    } finally {
      set(state => {
        return {
          ...state,
          isConfigLoading: false
        }
      })
    }
  },
  updateInterface: async (payload: Partial<UpdateInterfacePayloadType>) => {
    const { startLoading, finishWithSuccess, finishWithError } = useLoadingStore.getState()
    set(state => {
      return {
        ...state,
        isConfigLoading: true
      }
    })
    startLoading()
    try {
      const { interfaceConfig } = await POST.updateInterface(payload)
      set(state => {
        return {
          ...state,
          config: {
            ...state.config,
            interfaceConfig
          }
        }
      })
      finishWithSuccess()
    } catch (error: any) {
      if (error?.message === LARGE_IMAGE) {
        finishWithError(LARGE_IMAGE_ERROR_MESSAGE)
      } else {
        finishWithError()
      }
      consoleError(error)
    } finally {
      set(state => {
        return {
          ...state,
          isConfigLoading: false
        }
      })
    }
  },
  updateTableReservationConfig: async (payload: Partial<TableReservationConfigType>) => {
    const { startLoading, finishWithSuccess, finishWithError } = useLoadingStore.getState()
    set(state => {
      return {
        ...state,
        isConfigLoading: true
      }
    })
    startLoading()
    try {
      const { tableReservationConfig } = await POST.updateTableReservation(payload)
      set(state => {
        return {
          ...state,
          config: {
            ...state.config,
            tableReservationConfig
          }
        }
      })
      finishWithSuccess()
    } catch (error: any) {
      consoleError(error)
      finishWithError()
    } finally {
      set(state => {
        return {
          ...state,
          isConfigLoading: false
        }
      })
    }
  }
}))

export interface IConfigStore {
  isConfigLoading: boolean
  isConfigLoaded: boolean
  config: ConfigType
  loadConfig: (branchIdParam?: number) => void
  updateInterface: (payload: Partial<UpdateInterfacePayloadType>) => void
  updateMenuConfig: (payload: Partial<MenuConfigType>) => void
  updateTableReservationConfig: (payload: Partial<TableReservationConfigType>) => void
  updateLanguagesConfig: (language: AvailableLanguagesType | 'default') => void
}
